/**
 * Broadcast Connection Manager
 *
 * Handles peer connection to the broadcast host.
 * Manages connection lifecycle, timeouts, and connection state.
 *
 * This is feature-specific to Quickstart and knows about:
 * - Playcast peer connection
 * - Broadcast ID from matchmaking
 * - Connection timeouts
 */

import { connectPeer } from '../../../api/modules/player';
import { mediaStreamState, playerConnectionState } from '../../../api/stores/playerStores';
import type { QuickstartState } from '../QuickstartController';

export interface BroadcastConnectionManagerConfig {
  /**
   * Get current state
   */
  getState: () => QuickstartState;

  /**
   * Update state
   */
  updateState: (state: Partial<QuickstartState>) => void;

  /**
   * Transition to a new phase
   */
  transitionTo: (phase: string) => void;

  /**
   * Called when connection succeeds
   */
  onConnected: () => void;

  /**
   * Called when connection fails
   */
  onConnectionFailed: (reason: string) => void;

  /**
   * Called when peer disconnects during play (after successful connection)
   */
  onDisconnected?: (kickReason: string) => void;

  /**
   * Send analytics events
   */
  sendBroadcastJoinAttempted: () => void;
  sendBroadcastJoinFailed: (
    reason: string,
    duration: number,
    errorStage?: 'signaling' | 'sdp' | 'ice' | 'dtls' | 'auth' | 'timeout' | 'unknown'
  ) => void;
  sendBroadcastJoinSucceeded: (duration: number) => void;
}

/**
 * Manager for broadcast peer connection
 */
export class BroadcastConnectionManager {
  private static readonly VIDEO_WAIT_TIMEOUT_MS = 5000;

  private config: BroadcastConnectionManagerConfig;
  private connectionTimeout?: NodeJS.Timeout;
  private connectionListenerId?: string;
  private videoWaitListenerId?: string;
  private videoWaitTimeout?: NodeJS.Timeout;

  constructor(config: BroadcastConnectionManagerConfig) {
    this.config = config;
  }

  /**
   * Start connecting to broadcast
   */
  public connectToBroadcast(): void {
    const state = this.config.getState();
    const broadcastId = state.broadcastId;

    if (!broadcastId) {
      console.error('[BroadcastConnectionManager] No broadcast ID');
      this.config.onConnectionFailed('No broadcast ID');
      return;
    }

    console.log('[BroadcastConnectionManager] Connecting to broadcast:', broadcastId);

    this.config.updateState({
      connectionStartTime: Date.now(),
    });

    this.config.transitionTo('joining_broadcast');
    this.config.sendBroadcastJoinAttempted();

    // Clear any stale timeout from a previous attempt
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
    }

    // Set up connection timeout (30 seconds)
    this.connectionTimeout = setTimeout(() => {
      console.error('[BroadcastConnectionManager] Connection timeout');
      this.config.onConnectionFailed('Connection timeout');
    }, 30000);

    // Remove any stale listener from a previous connectToBroadcast call (e.g. reconnect)
    // to prevent duplicate callbacks from stacked listeners
    if (this.connectionListenerId !== undefined) {
      playerConnectionState.removeListener(this.connectionListenerId);
      this.connectionListenerId = undefined;
    }

    // Subscribe to player connection state
    this.connectionListenerId = playerConnectionState.addListener((newState) => {
      console.log('[BroadcastConnectionManager] Connection state:', newState.status);

      const currentState = this.config.getState();
      const wasConnected = currentState.playerConnectionId !== undefined;

      if (newState.status === 'connected') {
        this.handleConnectionSuccess(newState.playerPeerId);
      } else if (newState.status === 'failed' || newState.status === 'refused') {
        // Initial connection failure (before we were ever connected)
        this.handleConnectionFailure(newState.kickReason || 'Connection failed');
      } else if (newState.status === 'disconnected' && wasConnected) {
        // Mid-play disconnect (we were connected, now we're not)
        console.log('[BroadcastConnectionManager] Peer disconnected during play:', newState.kickReason);
        this.config.onDisconnected?.(newState.kickReason || 'unknown');
      } else if (newState.status === 'disconnected' && !wasConnected) {
        // Initial connection failure (disconnected before we connected)
        this.handleConnectionFailure(newState.kickReason || 'Connection failed');
      }
    });

    // Initiate connection
    connectPeer(broadcastId);
  }

  /**
   * Handle successful connection
   */
  private handleConnectionSuccess(playerPeerId?: string): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = undefined;
    }

    console.log('[BroadcastConnectionManager] Connection succeeded:', playerPeerId);

    const state = this.config.getState();
    const duration = state.connectionStartTime ? Date.now() - state.connectionStartTime : 0;

    this.config.updateState({
      playerConnectionId: playerPeerId,
    });

    this.config.sendBroadcastJoinSucceeded(duration);

    this.waitForVideoThenConnect();
  }

  private waitForVideoThenConnect(): void {
    const currentStream = mediaStreamState.currentState.stream;
    if (currentStream.getVideoTracks().length > 0) {
      console.log('[BroadcastConnectionManager] Video tracks already available, transitioning immediately');
      this.config.onConnected();
      return;
    }

    console.log('[BroadcastConnectionManager] Waiting for video tracks before transitioning...');

    this.videoWaitTimeout = setTimeout(() => {
      console.warn('[BroadcastConnectionManager] Video wait timed out, transitioning anyway');
      this.clearVideoWait();
      this.config.onConnected();
    }, BroadcastConnectionManager.VIDEO_WAIT_TIMEOUT_MS);

    this.videoWaitListenerId = mediaStreamState.addListener((newState) => {
      if (newState.stream.getVideoTracks().length > 0) {
        console.log('[BroadcastConnectionManager] Video tracks received, transitioning');
        this.clearVideoWait();
        this.config.onConnected();
      }
    });
  }

  private clearVideoWait(): void {
    if (this.videoWaitTimeout) {
      clearTimeout(this.videoWaitTimeout);
      this.videoWaitTimeout = undefined;
    }
    if (this.videoWaitListenerId !== undefined) {
      mediaStreamState.removeListener(this.videoWaitListenerId);
      this.videoWaitListenerId = undefined;
    }
  }

  /**
   * Handle connection failure
   */
  private handleConnectionFailure(reason: string): void {
    // Clear timeout
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = undefined;
    }

    // Unsubscribe from connection state
    if (this.connectionListenerId !== undefined) {
      playerConnectionState.removeListener(this.connectionListenerId);
      this.connectionListenerId = undefined;
    }

    console.error('[BroadcastConnectionManager] Connection failed:', reason);

    // Calculate connection duration
    const state = this.config.getState();
    const duration = state.connectionStartTime ? Date.now() - state.connectionStartTime : 0;

    // Determine error stage from reason
    let errorStage: 'signaling' | 'sdp' | 'ice' | 'dtls' | 'auth' | 'timeout' | 'unknown' = 'unknown';
    const lowerReason = reason.toLowerCase();
    if (lowerReason.includes('timeout')) {
      errorStage = 'timeout';
    } else if (lowerReason.includes('ice')) {
      errorStage = 'ice';
    } else if (lowerReason.includes('sdp')) {
      errorStage = 'sdp';
    } else if (lowerReason.includes('signal')) {
      errorStage = 'signaling';
    } else if (lowerReason.includes('auth')) {
      errorStage = 'auth';
    } else if (lowerReason.includes('dtls')) {
      errorStage = 'dtls';
    }

    this.config.sendBroadcastJoinFailed(reason, duration, errorStage);
    this.config.onConnectionFailed(reason);
  }

  /**
   * Cleanup connection resources
   */
  public cleanup(): void {
    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = undefined;
    }

    if (this.connectionListenerId !== undefined) {
      playerConnectionState.removeListener(this.connectionListenerId);
      this.connectionListenerId = undefined;
    }

    this.clearVideoWait();
  }
}

